// Hosted requests use same-origin HttpOnly sessions. Only the separate local
// build retains its existing loopback token workflow.

const API_BASE_KEY = "cloudpush_api_base";
const TOKEN_KEY = "cloudpush_token";

// Two builds share this wrapper.
//  - the default (hosted) build: the API base defaults to the hosted API, exactly as before;
//  - `vite build --mode local` (the owner's local app): the default is the loopback API and there is NO hosted fallback.
//    The hosted address is injected by vite.config.ts only into non-local builds, so it is not in the local bundle at all.
//    In local mode the dashboard token is only ever sent to a loopback address.
export const LOCAL_API_BASE = "http://127.0.0.1:8787";

export function isLocalMode(): boolean {
  return typeof __CLOUDPUSH_MODE__ !== "undefined" && __CLOUDPUSH_MODE__ === "local";
}

/** http(s) to 127.0.0.1, localhost or [::1] (any port): the only addresses the local build will talk to. */
export function isLoopbackBase(value: string): boolean {
  try {
    const u = new URL(value);
    return (u.protocol === "http:" || u.protocol === "https:") && ["127.0.0.1", "localhost", "[::1]"].includes(u.hostname) && u.username === "" && u.password === "";
  } catch {
    return false;
  }
}

export function defaultApiBase(): string {
  if (isLocalMode()) return LOCAL_API_BASE;
  return typeof __CLOUDPUSH_HOSTED_API_BASE__ === "string" ? __CLOUDPUSH_HOSTED_API_BASE__ : "";
}

export function getApiBase(): string {
  const stored = localStorage.getItem(API_BASE_KEY);
  // Local build: a stored non-loopback value (an earlier hosted session, another tool) is ignored, never used for the token.
  if (isLocalMode()) return stored !== null && isLoopbackBase(stored) ? stored.replace(/\/+$/, "") : LOCAL_API_BASE;
  return `${window.location.origin}/api`;
}
/** Local build: refuses anything but a loopback address and returns false. */
export function setApiBase(url: string): boolean {
  if (isLocalMode() && !isLoopbackBase(url)) return false;
  localStorage.setItem(API_BASE_KEY, url);
  return true;
}
export function getToken(): string | null {
  return isLocalMode() ? localStorage.getItem(TOKEN_KEY) : null;
}
export function setToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
}
export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export class ApiError extends Error {
  status: number;
  /**
   * Machine-readable error code from the response body (for example "OUTCOME_UNKNOWN"),
   * when the server sent one. Added for callers that must distinguish refusals;
   * `new ApiError(status, message)` and every existing caller are unaffected.
   */
  code?: string;
  /** Machine-readable reason from the body (for example "outcome_unknown"), when the server sent one. */
  reason?: string;
  /**
   * The parsed JSON error body, for callers that show structured refusals (skipped/failed destinations, constraint
   * errors, requiresReconciliation...). Treat every field as untrusted input.
   */
  body?: Record<string, unknown>;
  constructor(status: number, message: string, code?: string, extra?: { reason?: string; body?: Record<string, unknown> }) {
    super(message);
    this.status = status;
    this.code = code;
    this.reason = extra?.reason;
    this.body = extra?.body;
  }
}

// What the last real API call said about the connection (local build header and login). A caller never has to ask.
// "restarting" and "failed" are what the local supervisor says (503 {ok:false, status}) while its API process is away or has been given up on:
// not "cannot reach", because something answered and said why.
export type ConnectionEvent = "reachable" | "unreachable" | "unauthorized" | "forbidden" | "restarting" | "failed";
export interface ConnectionDetail {
  restarts?: number;
  reason?: string;
}
type Listener = (event: ConnectionEvent, detail?: ConnectionDetail) => void;
const listeners = new Set<Listener>();
export function onConnectionEvent(fn: Listener): () => void {
  listeners.add(fn);
  return () => void listeners.delete(fn);
}
const announce = (event: ConnectionEvent, detail?: ConnectionDetail) => listeners.forEach((fn) => fn(event, detail));

/** The supervisor's own answer while the API process is away: 503 {ok:false, status:"restarting"|"failed", error, restarts}. Anything else is not it. */
export function supervisorStatus(status: number, json: unknown): { event: "restarting" | "failed"; detail: ConnectionDetail } | null {
  if (status !== 503 || typeof json !== "object" || json === null) return null;
  const j = json as Record<string, unknown>;
  if (j.ok !== false || (j.status !== "restarting" && j.status !== "failed")) return null;
  return { event: j.status, detail: { ...(typeof j.restarts === "number" ? { restarts: j.restarts } : {}), ...(typeof j.error === "string" ? { reason: j.error.slice(0, 300) } : {}) } };
}

export async function api<T = any>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken();
  let res: Response;
  try {
    res = await fetch(`${getApiBase()}${path}`, {
      ...init,
      credentials: isLocalMode() ? "omit" : "same-origin",
      headers: {
        "Content-Type": "application/json",
        ...(!isLocalMode() ? { "X-CloudPush-Session": "1" } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(init?.headers ?? {}),
      },
    });
  } catch (e) {
    // An aborted request says nothing about the connection. Anything else (the browser could not connect) does.
    if (!(e instanceof DOMException && e.name === "AbortError")) announce("unreachable");
    throw e;
  }
  const json = await res.json().catch(() => ({}));
  const sup = supervisorStatus(res.status, json);
  if (sup) announce(sup.event, sup.detail);
  else announce(res.status === 401 ? "unauthorized" : res.status === 403 ? "forbidden" : "reachable");
  if (!res.ok) {
    // A non-string `error` (object, array) must not become "[object Object]" on screen.
    const message = typeof json?.error === "string" && json.error ? json.error : `HTTP ${res.status}`;
    const code = typeof json?.code === "string" ? json.code : undefined;
    const reason = typeof json?.reason === "string" ? json.reason : undefined;
    throw new ApiError(res.status, message, code, { reason, body: typeof json === "object" && json !== null && !Array.isArray(json) ? (json as Record<string, unknown>) : undefined });
  }
  return json as T;
}

/** Public, isolated API origin for signed HTML previews and the MCP endpoint. */
export const publicApiBase = () => isLocalMode() ? getApiBase() : defaultApiBase();
export async function hasSession(): Promise<boolean> {
  if (isLocalMode()) return !!getToken();
  // Discard the previous hosted owner's reusable browser token during migration.
  clearToken();
  localStorage.removeItem(API_BASE_KEY);
  try { await api("/auth/session"); return true; }
  catch (error) { if (error instanceof ApiError && error.status === 401) return false; throw error; }
}
export async function signOut(allDevices = false): Promise<void> {
  if (!isLocalMode()) await api(allDevices ? "/auth/logout-all" : "/auth/logout", { method:"POST" });
  clearToken();
}
